/**
 * aw26-sticky-cta.js
 * Shared contract layer for the AW26 mobile sticky bars: the PDP buy bar
 * (snippets/aw26-sticky-buy-bar.liquid) and the cart checkout bar
 * (snippets/aw26-sticky-checkout-bar.liquid).
 *
 * Each bar keeps its own show/hide logic and only toggles .is-visible on
 * itself. This module watches every [data-aw26-sticky-cta] bar and publishes
 * the two things other floating UI needs to stay clear of it:
 *
 *   <body class="aw26-sticky-cta-visible">   while a bar is really on screen
 *   :root { --aw26-sticky-cta-height: 72px } the measured height of that bar
 *
 * Consumer today: the newsletter teaser pill in assets/aw26-newsletter-popup.css,
 * which lifts itself by that height instead of landing on top of the bar.
 * Both signals are removed again the moment no bar is on screen, so nothing
 * lingers on desktop, on other templates, or after the bar scrolls away.
 */
(function () {
  if (window.__aw26StickyCtaContract) return;
  window.__aw26StickyCtaContract = true;

  var BODY_CLASS = 'aw26-sticky-cta-visible';
  var HEIGHT_VAR = '--aw26-sticky-cta-height';
  var BAR_SELECTOR = '[data-aw26-sticky-cta]';
  var frame = null;

  /** The first bar that is both marked visible and actually rendered. */
  function currentBar() {
    var bars = document.querySelectorAll(BAR_SELECTOR);
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      if (!bar.classList.contains('is-visible')) continue;
      // Measured, never assumed: a display:none box has no client rects, and
      // both bars are display:none from 750px up - so desktop measures 0 and
      // publishes nothing. (Deliberately not offsetParent: that is null for
      // every position:fixed element, on screen or not.)
      var height = bar.getClientRects().length ? Math.round(bar.getBoundingClientRect().height) : 0;
      if (height > 0) return { bar: bar, height: height };
    }
    return null;
  }

  function publish() {
    frame = null;
    var current = currentBar();
    if (current) {
      document.body.classList.add(BODY_CLASS);
      document.documentElement.style.setProperty(HEIGHT_VAR, current.height + 'px');
    } else {
      document.body.classList.remove(BODY_CLASS);
      document.documentElement.style.removeProperty(HEIGHT_VAR);
    }
  }

  function schedule() {
    if (frame) return;
    frame = window.requestAnimationFrame(publish);
  }

  /* Watch each bar directly instead of the whole document: the only mutations
     that matter are its own class toggle and its own height. */
  var classObserver = new MutationObserver(schedule);
  var sizeObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : null;

  function bind() {
    var bars = document.querySelectorAll(BAR_SELECTOR);
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      if (bar.dataset.aw26StickyCtaBound) continue;
      bar.dataset.aw26StickyCtaBound = 'true';
      classObserver.observe(bar, { attributes: true, attributeFilter: ['class'] });
      if (sizeObserver) sizeObserver.observe(bar);
    }
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind, { once: true });
  } else {
    bind();
  }

  // Crossing the 750px breakpoint or rotating the device changes whether the
  // bar renders at all, and the theme editor re-renders whole sections.
  window.addEventListener('resize', schedule);
  window.addEventListener('orientationchange', schedule);
  window.addEventListener('pageshow', bind);
  document.addEventListener('shopify:section:load', bind);
})();
